import {
  Button,
  Drawer,
  FieldLabel,
  FileUploader,
  Flex,
  Form,
  MoreButton,
  RadioGroupField,
  Text,
} from '@/components';
import { useTenantManagerPermission } from '@/features/authentication/_hooks/permission';
import {
  BulkUpdateUsersSchema,
  UsersCsvUploadRunMode,
  UsersForBulkUpdateSchema,
} from '@/features/tenant-manager/user/_models/user';
import { useMessage } from '@/providers';
import { UploadOutlined } from '@ant-design/icons';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useDownloadUserListCsv } from '../_hooks/user';
import { useUploadUserListCsv } from '../_hooks/user';
import { createUsersData, readFileAsText } from '../_utils/user';

const UploadUsersFileSchema = BulkUpdateUsersSchema.pick({
  runMode: true,
});

type UploadUsersFileDrawerProps = {
  open: boolean;
  onClose: () => void;
};

type FormValues = z.infer<typeof UploadUsersFileSchema>;

const UploadUsersFileDrawer: FC<UploadUsersFileDrawerProps> = ({
  open: openDrawer,
  onClose,
}) => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const { mutateAsync: uploadUserListCsv, isPending } = useUploadUserListCsv();

  const [usersFile, setUsersFile] = useState<File | null>(null);

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      runMode: UsersCsvUploadRunMode.Values['dry-run'],
    },
  });

  const onError = useCallback(
    (message: string) => {
      openMessage('error', message);
      setUsersFile(null);
    },
    [openMessage],
  );

  const doUploadUserListCsv = useCallback(
    async (csvData: (string | number)[][]) => {
      const runMode = getValues('runMode');
      try {
        const usersData = createUsersData(csvData);
        const parsedUsersData = UsersForBulkUpdateSchema.parse(usersData);
        try {
          await uploadUserListCsv({
            runMode,
            csvData: parsedUsersData,
          });
          openMessage(
            'success',
            t('export-file-requested', {
              target: t('upload-csv-file', {
                target: t('user-list'),
              }),
            }),
          );
          onClose();
        } catch {
          onError(t('error-message-with-file'));
        }
      } catch {
        onError(t('error-message-invalid-data'));
      }
    },
    [uploadUserListCsv, getValues, t, openMessage, onClose, onError],
  );

  const onSubmit = useMemo(
    () =>
      handleSubmit(() => {
        invariant(usersFile, t('unreachable-code'));
        readFileAsText(usersFile, {
          onError: () => {
            onError(t('error-message-invalid-reader-result'));
          },
          onSuccess: (result) => doUploadUserListCsv(result.data),
        });
      }),
    [handleSubmit, onError, t, doUploadUserListCsv, usersFile],
  );

  useEffect(() => {
    if (openDrawer) {
      reset();
      setUsersFile(null);
    }
  }, [openDrawer, reset]);

  return (
    <Drawer destroyOnClose open={openDrawer} onClose={onClose}>
      <Form
        leavePageAlertCondition={{
          isDirty,
          isSubmitting,
          isSubmitSuccessful,
        }}
        onFinish={onSubmit}
      >
        <Flex vertical gap={8}>
          <Text text={t('user-list-upload-type')} color="secondary" />
          <Text
            text={t('user-list-upload-select-upload-type')}
            color="secondary"
            style={{
              fontSize: 12,
            }}
          />
          <RadioGroupField
            name="runMode"
            control={control}
            errors={errors}
            options={[
              {
                value: UsersCsvUploadRunMode.Values['dry-run'],
                label: t('user-list-upload-test'),
                hint: t('user-list-uploaded-file-will-be-tested'),
              },
              {
                value: UsersCsvUploadRunMode.Values.save,
                label: t('user-list-upload-register'),
                hint: t('user-list-upload-with-uploaded-file'),
              },
            ]}
            optionsDirection="vertical"
          />
          <Flex vertical gap={24}>
            <Flex vertical gap={8}>
              <FieldLabel text={t('csv-file')} required />
              <FileUploader
                acceptableImage={false}
                acceptablePdf={false}
                withPreviewImage={false}
                maxFiles={1}
                files={usersFile ? [usersFile] : []}
                onChange={async (acceptedFiles) => {
                  const file = acceptedFiles[0];
                  if (file && file.type !== 'text/csv') {
                    await openMessage(
                      'error',
                      t('error-message-invalid-file-type'),
                    );
                    return;
                  }
                  setUsersFile(file ?? null);
                }}
                handleCancel={() => setUsersFile(null)}
                styles={{
                  inner: {
                    width: '100%',
                  },
                  button: {
                    width: '100%',
                    maxWidth: '100%',
                  },
                }}
              />
            </Flex>
            <Flex>
              <Button
                htmlType="submit"
                type="primary"
                loading={isPending}
                disabled={!usersFile}
                icon={<UploadOutlined />}
              >
                {t('upload')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Form>
    </Drawer>
  );
};

export const UsersActionsArea: FC = () => {
  const { t } = useTranslation();

  const { openMessage } = useMessage();

  const permission = useTenantManagerPermission(true);

  const { mutateAsync: downloadUserListCsv, isPending } =
    useDownloadUserListCsv();

  const [openUploadDrawer, setOpenUploadDrawer] = useState(false);

  const download = useCallback(async () => {
    try {
      await downloadUserListCsv();
      openMessage(
        'success',
        t('export-file-requested', {
          target: t('export-csv-file', {
            target: t('user-list-all'),
          }),
        }),
      );
    } catch {
      openMessage('error', t('error-message-csv-download'));
    }
  }, [downloadUserListCsv, openMessage, t]);

  return (
    <>
      <MoreButton
        items={[
          {
            key: 'csvDownload',
            label: t('csv-download'),
            disabled: isPending,
          },
          ...(permission?.data
            ? [
                {
                  key: 'csvUpload',
                  label: t('csv-upload'),
                },
              ]
            : []),
        ]}
        onClick={({ key }) => {
          switch (key) {
            case 'csvDownload':
              download();
              break;
            case 'csvUpload':
              return setOpenUploadDrawer(true);
          }
        }}
      />
      <UploadUsersFileDrawer
        open={openUploadDrawer}
        onClose={() => setOpenUploadDrawer(false)}
      />
    </>
  );
};
